/* eslint-disable jsx-a11y/label-has-associated-control */
// @flow

import * as React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

function ForgotPasswordForm({ setErrorMessage = 'hello world!', setSuccessMessage, forgotPasswordRequest }: any): React$Element<any> {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleReCaptchaVerify = React.useCallback(async () => {
    if (!executeRecaptcha) {
      return null;
    }

    const token = await executeRecaptcha('forgot_password');
    return token;
  }, [executeRecaptcha]);
  return (
    <Formik
      initialValues={{ email: '' }}
      validate={(values) => {
        const errors = {};
        if (!values.email) {
          errors.email = 'Required';
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
          errors.email = 'Invalid email address';
        }
        return errors;
      }}
      onSubmit={async (values, { setSubmitting, setFieldError }) => {
        try {
          // Get reCAPTCHA token
          const recaptchaToken = await handleReCaptchaVerify();

          if (!recaptchaToken) {
            setFieldError('general', 'reCAPTCHA verification failed. Please try again.');
            setSubmitting(false);
            return;
          }
          forgotPasswordRequest(values.email, recaptchaToken);
          setSubmitting(false);
        } catch (error) {
          console.error('reCAPTCHA error:', error);
          setFieldError('general', 'reCAPTCHA verification failed. Please try again.');
          setSubmitting(false);
        }
      }}
    >
      {({ errors, touched, isSubmitting }) => (
        <Form className="col-md-12 p-0">
          <div className="form-group">
            <label className="form-label d-block" htmlFor="email">
              Email Address:
            </label>
            <Field
              type="email"
              name="email"
              id="email"
              className={`form-control ${errors.email && touched.email ? 'is-invalid' : touched.email && 'is-valid'}`}
              placeholder="Enter your email address"
            />
            <ErrorMessage name="email" render={(msg) => <div className="invalid-feedback">{msg}</div>} />
            <small className="form-text text-muted">
              Enter the email address associated with your account. If it exists in our system, we'll send you a password reset email.
            </small>
          </div>
          {errors.general && (
            <div className="form-group">
              <div className="alert alert-soft-danger">
                <div className="d-flex flex-wrap">
                  <div className="mr-8pt">
                    <i className="material-icons">report</i>
                  </div>
                  <div className="flex" style={{ minWidth: '180px' }}>
                    <small className="text-100">{errors.general}</small>
                  </div>
                </div>
              </div>
            </div>
          )}

          {setSuccessMessage && (
            <div className="form-group">
              <div className="alert alert-soft-success">
                <div className="d-flex flex-wrap">
                  <div className="mr-8pt">
                    <i className="material-icons">check_circle</i>
                  </div>
                  <div className="flex" style={{ minWidth: '180px' }}>
                    <small className="text-100">{setSuccessMessage}</small>
                  </div>
                </div>
              </div>
            </div>
          )}

          {setErrorMessage && (
            <div className="form-group">
              <div className="alert alert-soft-danger">
                <div className="d-flex flex-wrap">
                  <div className="mr-8pt">
                    <i className="material-icons">report</i>
                  </div>
                  <div className="flex" style={{ minWidth: '180px' }}>
                    <small className="text-100">{setErrorMessage}</small>
                  </div>
                </div>
              </div>
            </div>
          )}

          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Send Reset Email'}
          </button>
          <div className="mt-2">
            <small className="text-muted">
              This site is protected by reCAPTCHA and the Google{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>{' '}
              and{' '}
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </a>{' '}
              apply.
            </small>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export default ForgotPasswordForm;
